import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, Users, Link2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", color: "#24A1DE", telegram_channel_id: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadGroups(); }, []);

  const loadGroups = async () => {
    try {
      const { data } = await api.get("/groups");
      setGroups(data.groups);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const addGroup = async (e) => {
    e.preventDefault();
    try {
      await api.post("/groups", form);
      toast.success("Grup olusturuldu");
      setShowAdd(false);
      setForm({ name: "", description: "", color: "#24A1DE", telegram_channel_id: "" });
      loadGroups();
    } catch (err) { toast.error(err.response?.data?.detail || "Hata"); }
  };

  const deleteGroup = async (id) => {
    try {
      await api.delete(`/groups/${id}`);
      toast.success("Grup silindi");
      loadGroups();
    } catch (err) { toast.error("Silme basarisiz"); }
  };

  const generateInvite = async (id) => {
    try {
      const { data } = await api.post(`/groups/${id}/invite-link`);
      toast.success("Davet linki olusturuldu");
      loadGroups();
    } catch (err) { toast.error(err.response?.data?.detail || "Link olusturulamadi"); }
  };

  const colors = ["#24A1DE", "#34C759", "#FF9F0A", "#FF3B30", "#AF52DE"];

  return (
    <div data-testid="groups-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Gruplar</h1>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button data-testid="add-group-btn" className="bg-[#24A1DE] hover:brightness-110 text-white">
              <Plus className="w-4 h-4 mr-2" />Grup Olustur
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-[#27272A]">
            <DialogHeader>
              <DialogTitle className="text-white font-heading">Yeni Grup</DialogTitle>
            </DialogHeader>
            <form onSubmit={addGroup} className="space-y-4">
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Grup Adi</Label>
                <Input data-testid="group-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-[#2C2C2E] border-transparent text-white" required />
              </div>
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Aciklama</Label>
                <Input data-testid="group-description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} className="bg-[#2C2C2E] border-transparent text-white" />
              </div>
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Telegram Kanal ID (opsiyonel)</Label>
                <Input data-testid="group-channel-id" value={form.telegram_channel_id} onChange={(e) => setForm({...form, telegram_channel_id: e.target.value})} className="bg-[#2C2C2E] border-transparent text-white font-mono" placeholder="-100..." />
              </div>
              <div className="space-y-1">
                <Label className="text-[#A1A1AA] text-sm">Renk</Label>
                <div className="flex gap-2 mt-1">
                  {colors.map(c => (
                    <button key={c} type="button" onClick={() => setForm({...form, color: c})}
                      className={`w-8 h-8 rounded-md border-2 transition-all ${form.color === c ? "border-white scale-110" : "border-transparent"}`}
                      style={{ backgroundColor: c }}
                      data-testid={`color-${c}`}
                    />
                  ))}
                </div>
              </div>
              <Button data-testid="submit-group" type="submit" className="w-full bg-[#24A1DE] hover:brightness-110">Olustur</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map(group => (
          <Card key={group.id} className="bg-[#121212] border-[#27272A] hover:border-[#3f3f46] transition-colors duration-150" data-testid={`group-card-${group.id}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: group.color }} />
                  <CardTitle className="text-white text-base font-heading">{group.name}</CardTitle>
                </div>
                <Button data-testid={`delete-group-${group.id}`} variant="ghost" size="sm" onClick={() => deleteGroup(group.id)} className="text-[#A1A1AA] hover:text-[#FF3B30] h-8 w-8 p-0">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-[#A1A1AA] text-sm mb-3">{group.description || "Aciklama yok"}</p>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-[#A1A1AA] text-sm">
                  <Users className="w-4 h-4" />
                  <span>{group.user_count || 0} kullanici</span>
                </div>
                {group.telegram_channel_id && (
                  <Button data-testid={`invite-group-${group.id}`} variant="outline" size="sm" onClick={() => generateInvite(group.id)} className="border-[#27272A] text-[#24A1DE] text-xs h-7 hover:bg-[#2C2C2E]">
                    <Link2 className="w-3 h-3 mr-1" />Davet
                  </Button>
                )}
              </div>
              {group.invite_link && (
                <p className="text-xs font-mono text-[#24A1DE] mt-2 truncate">{group.invite_link}</p>
              )}
            </CardContent>
          </Card>
        ))}
        {groups.length === 0 && !loading && (
          <div className="col-span-full text-center text-[#A1A1AA] py-16 text-sm">Henuz grup olusturulmamis</div>
        )}
      </div>
    </div>
  );
}
